<script setup lang="ts">
import { UiText, UiIcon, UiTooltip } from "@shopexperts/shared";
import { formatCurrency } from "@/v2Projects/utils/display";
import type { BreakdownRow } from "./useWalletTransaction";
import type { ProviderFeeDetailLine } from "./walletTransactionDisplay";

defineProps<{
  rows: BreakdownRow[];
  providerFeeLines: ProviderFeeDetailLine[];
  currency: string | null;
}>();
</script>

<template>
  <dl class="flex flex-col gap-2.5">
    <template v-for="row in rows" :key="row.key">
      <div
        class="flex items-baseline justify-between gap-4"
        :class="row.tone === 'total' ? 'mt-1 border-t border-dashed border-se-dark/20 pt-3' : ''"
      >
        <dt class="flex items-center gap-1">
          <UiText
            as="span"
            variant="small"
            :text="row.label"
            :class="['text-se-dark', row.tone === 'total' ? 'font-semibold' : '']"
          />
          <UiTooltip v-if="row.tooltip" :content="row.tooltip">
            <UiIcon name="info-circle" class="h-3.5 w-3.5 shrink-0 text-se-dark" />
          </UiTooltip>
        </dt>
        <UiText
          as="span"
          variant="small"
          :text="row.value"
          class="shrink-0 font-semibold tabular-nums text-se-dark"
        />
      </div>

      <div
        v-if="row.key === 'provider_fee' && providerFeeLines.length"
        class="ml-1 flex flex-col gap-1 border-l border-se-dark py-0.5 pl-3"
      >
        <div
          v-for="line in providerFeeLines"
          :key="line.key"
          class="flex items-baseline justify-between gap-4"
        >
          <UiText
            as="span"
            variant="caption-normal"
            :text="line.label"
            class="min-w-0 truncate text-se-dark"
          />
          <UiText
            v-if="line.amountCents !== null"
            as="span"
            variant="caption-normal"
            :text="`−${formatCurrency(line.amountCents, line.currency ?? currency)}`"
            class="shrink-0 tabular-nums text-se-dark"
          />
        </div>
      </div>
    </template>
  </dl>
</template>
